import { pageKeyboard, type KeyboardUse } from "../controls/keyboard-use.ts";
import { isTouchPage, watchTouchPage, type MediaWindow } from "./page.ts";

/**
 * Whether the page names keys (ADR-061's amendment of 2026-10-31, decision 12; the author's ruling of 2026-10-07): a
 * page that is not a touch page always does, and a touch page does from the first hardware key press. A world prompt
 * draws its key cap, and a button its key, only while this holds; otherwise the action alone is shown.
 *
 * @param touch Whether the page is a touch page.
 * @param keyboardSeen Whether a hardware key press has been seen.
 * @returns True when keys may be named.
 */
export function keyHintsShown(touch: boolean, keyboardSeen: boolean): boolean {
  return !touch || keyboardSeen;
}

/**
 * The page's answer to "may a key be named", kept current and announced when it changes, for a surface that draws
 * once and follows (the canvas prompt) and a React store (`useSyncExternalStore`).
 */
export class KeyHints {
  private touch: boolean;
  private readonly listeners = new Set<() => void>();
  private readonly stops: (() => void)[] = [];

  /**
   * @param keyboard The keyboard-use record; the page's by default.
   * @param source The window to ask whether the page is a touch page; the page's own by default.
   */
  public constructor(private readonly keyboard: Pick<KeyboardUse, "seen" | "subscribe"> = pageKeyboard, source?: MediaWindow) {
    this.touch = isTouchPage(source);
    this.stops.push(
      watchTouchPage((touch) => {
        this.touch = touch;
        this.announce();
      }, source),
      this.keyboard.subscribe(() => this.announce()),
    );
  }

  /**
   * Whether a key may be named now. One function for the object's life, for a React store.
   *
   * @returns True when keys are shown.
   */
  public readonly getSnapshot = (): boolean => keyHintsShown(this.touch, this.keyboard.seen);

  /**
   * Listens for a change of the answer. One function for the object's life, for a React store.
   *
   * @param listener Called after the answer may have changed.
   * @returns A function that stops listening.
   */
  public readonly subscribe = (listener: () => void): (() => void) => {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  };

  /**
   * Whether the page is a touch page now.
   *
   * @returns True on a touch page.
   */
  public get touchPage(): boolean {
    return this.touch;
  }

  /**
   * Stops following the page.
   */
  public destroy(): void {
    for (const stop of this.stops.splice(0)) {
      stop();
    }
    this.listeners.clear();
  }

  /**
   * Tells every listener.
   */
  private announce(): void {
    for (const listener of [...this.listeners]) {
      listener();
    }
  }
}

/**
 * The page's key hints.
 */
export const pageKeyHints = new KeyHints();
